import { ReactElement, useMemo, useState, MouseEvent, SyntheticEvent } from 'react'
import { useMatch } from 'react-router-dom'
import Cloud from '@mui/icons-material/Cloud'
import {
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Stack,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material'
import { InfoTooltip } from '@ui/feedback'
import { DraggablePopup, HeaderBadge } from '@ui/structure'
import useUIStore from 'src/state/stores/ui'
import ActivitiesTab from './ActivitiesTab'
import ServerTab from './ServerTab'
import { isDocumentTask } from './documentTasks'
import useRunningTasks from './useRunningTasks'

type WidgetTab = 'activities' | 'server'

const RequestsWidget = (): ReactElement => {
  const interactions = useUIStore(state => state.interactions)
  const { runningTasks } = useRunningTasks()
  const isDocumentManagerPage = !!useMatch('/editor/:projectId/document-manager')

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [activeTab, setActiveTab] = useState<WidgetTab>('activities')

  const errorCount = useMemo(
    () => interactions.filter(interaction => interaction.state === 'failed').length,
    [interactions],
  )

  const runningCount = useMemo(
    () => interactions.filter(interaction => interaction.state === 'running').length,
    [interactions],
  )

  // The document manager footer already shows the running document tasks with their
  // progress, so they don't drive the spinner while that page is open
  const spinnerTasks = useMemo(
    () =>
      isDocumentManagerPage ? runningTasks.filter(task => !isDocumentTask(task)) : runningTasks,
    [isDocumentManagerPage, runningTasks],
  )

  const hasRunningTasks = runningCount > 0 || spinnerTasks.length > 0

  const handleToggle = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(prev => (prev ? null : event.currentTarget))
  }

  const handleTabChange = (_event: SyntheticEvent, newTab: WidgetTab) => {
    setActiveTab(newTab)
  }

  return (
    <>
      <Tooltip title="Ereignisse">
        <IconButton
          size="small"
          aria-label="Ereignisse"
          sx={{
            color: 'rgba(0, 0, 0, 0.54)',
            width: 32,
            height: 32,
            '& .MuiCircularProgress-root': {
              color: 'primary.main',
            },
          }}
          onClick={handleToggle}
        >
          {hasRunningTasks ? (
            <CircularProgress size={15} />
          ) : (
            <HeaderBadge
              color={errorCount > 0 ? 'error' : 'primary'}
              badgeContent={errorCount}
              invisible={errorCount === 0}
            >
              <Cloud sx={{ fontSize: 16 }} />
            </HeaderBadge>
          )}
        </IconButton>
      </Tooltip>

      <DraggablePopup
        title={
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Box>
              <Typography variant="h6">Ereignisse</Typography>
            </Box>
            <InfoTooltip
              text="Interaktionen mit dem Backend"
              secondLineText={`Hier können Sie eine Auswahl der letzten Interaktionen mit dem Backend sehen.\nAchtung: nur manche Ereignisse werden hier angezeigt.`}
            />
          </Stack>
        }
        closePopover={() => setAnchorEl(null)}
        anchorEl={anchorEl || undefined}
        paperSx={{
          width: '600px',
          maxWidth: '90vw',
          maxHeight: '80vh',
          overflow: 'auto',
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Aktivitäten" value="activities" />
            <Tab
              label={runningTasks.length > 0 ? `Server (${runningTasks.length})` : 'Server'}
              value="server"
            />
          </Tabs>
        </Box>

        <Box sx={{ p: 2 }}>
          {activeTab === 'activities' ? (
            <ActivitiesTab interactions={interactions} />
          ) : (
            <ServerTab runningTasks={runningTasks} />
          )}
        </Box>
      </DraggablePopup>
    </>
  )
}

export default RequestsWidget
